/**
 * =============================================================================
 * animations.js — We Are Rewind
 * =============================================================================
 * Toutes les animations du thème, en GSAP 3.14 (gsap + ScrollTrigger + SplitText,
 * déjà chargés par les layouts). Ce fichier remplace :
 *   - Webflow IX3 : parallaxes, hovers, accordéons FAQ,
 *     « Lire plus »… (interactions exportées par Liquify dans webflow.js) ;
 *   - Webflow IX2 : ouverture / fermeture des dropdowns (menu, langue,
 *     recherche, mini-panier, modale filtres) et tilt de la carte cadeau ;
 *   - custom-animations.js : hover des liens de nav et des catégories, titre de
 *     la page À propos.
 *
 * Les APPARITIONS de sections (au chargement et au scroll) ne sont pas ici :
 * elles passent par les attributs data-reveal / data-reveal-children, rendus en
 * CSS (snippets/global_styles.liquid) et déclenchés par assets/reveal.js. Les
 * reveals IX3, l'intro GSAP de la home, le texte mot à mot des sections image +
 * texte et le glissement du visuel savoir-faire ont été remplacés par ce système.
 *
 * Chaque bloc reproduit à l'identique la timeline que le moteur Webflow
 * construisait : cibles, durées, eases, positions, staggers et réglages
 * ScrollTrigger ont été relevés sur le site live (instance IX3 inspectée le
 * 2026-10-01). L'id Webflow d'origine est cité en commentaire (i-/t- pour IX3,
 * e-/a- pour IX2). Détail et écarts volontaires : docs/animations.md.
 *
 * API : window.WARAnimations.refresh()
 *   Branche les éléments apparus depuis le chargement et libère ceux qui ont
 *   quitté le DOM. Appelé automatiquement après les rendus Liquify
 *   (`liquify:sections-rendered`) ; à appeler à la main après tout autre
 *   remplacement de DOM (ex. collection_filters.js).
 * =============================================================================
 */
(function () {
  'use strict';

  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  const SplitText = window.SplitText;
  if (!gsap || !ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);
  if (SplitText) gsap.registerPlugin(SplitText);

  /* Breakpoints Webflow : main ≥ 992 px, medium 768–991, small 480–767, tiny < 480.
     Les interactions IX3 réglées « dont-animate » sur medium/small/tiny n'existent
     qu'en desktop ; elles sont annulées (styles inline retirés) sous 992 px. */
  const ALL = 'all';
  const DESKTOP = '(min-width: 992px)';
  const TABLET_DOWN = '(max-width: 991px)';

  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const isDesktop = () => window.matchMedia(DESKTOP).matches;

  /* IX3 posait force3D:true sur chacun de ses tweens. */
  const ix = (vars) => Object.assign({ force3D: true }, vars);

  /* ===========================================================================
   * Moteur minimal
   * Une « interaction » = un sélecteur déclencheur + une media query + une
   * fabrique qui construit, pour UN élément déclencheur, sa timeline (et son
   * ScrollTrigger éventuel). Comme IX3, les instances sont créées dès
   * l'initialisation pour que les états initiaux (from / fromTo) soient posés,
   * et toutes vivent dans le gsap.Context de leur media query : gsap.matchMedia
   * les annule proprement quand on change de breakpoint.
   * ======================================================================== */
  const interactions = [];
  const scopes = new Map(); // query -> { ctx, instances: Map<def, WeakMap<el, inst>>, live: inst[] }
  const matchMedia = gsap.matchMedia();

  function define(query, selector, create, events) {
    interactions.push({ query, selector, create, events: events || {} });
  }

  function instanceFor(def, el) {
    const scope = scopes.get(def.query);
    if (!scope) return null; // breakpoint inactif
    let byEl = scope.instances.get(def);
    if (!byEl) scope.instances.set(def, (byEl = new WeakMap()));
    let inst = byEl.get(el);
    if (!inst) {
      inst = {};
      // Un `revert` éventuel nettoie ce que GSAP n'a pas posé lui-même, quand la
      // media query se désactive (gsap.context appelle la fonction retournée).
      scope.ctx.add(() => Object.assign(inst, def.create(el)).revert);
      inst.el = el;
      byEl.set(el, inst);
      scope.live.push(inst);
    }
    return inst;
  }

  function bind(scope) {
    interactions.forEach((def) => {
      if (def.query === scope.query) $$(def.selector).forEach((el) => instanceFor(def, el));
    });
  }

  function activate(query) {
    const setup = (ctx) => {
      const scope = { query, ctx, instances: new Map(), live: [] };
      scopes.set(query, scope);
      bind(scope);
      return () => scopes.delete(query);
    };
    if (query === ALL) setup(gsap.context(() => {}));
    else matchMedia.add(query, setup);
  }

  function refresh() {
    scopes.forEach((scope) => {
      scope.live = scope.live.filter((inst) => {
        if (inst.el.isConnected) return true;
        if (inst.st) inst.st.kill();
        if (inst.tl) inst.tl.kill();
        return false;
      });
      bind(scope);
    });
    // Le DOM a changé : on recalcule les positions de tous les ScrollTriggers.
    ScrollTrigger.refresh();
  }

  /* Contrôles de lecture IX3 (coordinator.play/reverse/restart/togglePlayReverse).
     IX3 réglait le timeScale à chaque déclenchement (speed, 1 par défaut). */
  const control = {
    restart: (tl, speed) => tl.timeScale(speed || 1).restart(),
    reverse: (tl, speed) => tl.timeScale(speed || 1).reverse(),
    togglePlayReverse(tl, speed) {
      tl.timeScale(speed || 1);
      const p = tl.progress();
      if (p === 0) tl.play();
      else if (p === 1) tl.reverse();
      else if (tl.reversed()) tl.play();
      else tl.reverse();
    },
  };

  /* Survol : IX3 « hover each » sans contrôle explicite = restart() à l'entrée,
     reverse() à la sortie. mouseenter/mouseleave ne remontent pas : on les
     écoute en phase de capture sur document, ce qui couvre aussi les éléments
     injectés après coup (IX3 ne les liait pas). */
  document.addEventListener(
    'mouseenter',
    (e) => {
      const el = e.target;
      if (!(el instanceof Element)) return;
      interactions.forEach((def) => {
        if (!def.events.hover || !el.matches(def.selector)) return;
        const inst = instanceFor(def, el);
        if (!inst || !inst.tl) return;
        if (inst.beforeEnter) inst.beforeEnter();
        control.restart(inst.tl);
      });
    },
    { capture: true, passive: true }
  );
  document.addEventListener(
    'mouseleave',
    (e) => {
      const el = e.target;
      if (!(el instanceof Element)) return;
      interactions.forEach((def) => {
        if (!def.events.hover || !el.matches(def.selector)) return;
        const inst = instanceFor(def, el);
        if (inst && inst.tl) control.reverse(inst.tl, def.events.hover.leaveSpeed);
      });
    },
    { capture: true, passive: true }
  );

  /* Clic : délégué sur document (IX3 délégait aussi, mais seulement pour les
     éléments présents à l'init). */
  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    interactions.forEach((def) => {
      if (!def.events.click) return;
      const el = target.closest(def.selector);
      if (!el) return;
      const inst = instanceFor(def, el);
      if (inst && inst.tl) def.events.click(inst.tl);
    });
  });

  /* ScrollTrigger avec scrub : la timeline suit le scroll.
     Parallaxes d'images : scrub true (lié directement au scroll), et non le
     lissage 0,8 s d'IX3. Lenis lisse déjà le scroll ; les deux lissages cumulés
     faisaient arriver les images décalées en scroll rapide, puis glisser en
     place avec retard (ex. « Notre histoire », page À propos). */
  const scrubWith = (trigger, tl, start, end, scrub) =>
    ScrollTrigger.create({ trigger, start, end, scrub, animation: tl });

  /* ===========================================================================
   * IX3 — parallaxes et effets liés au scroll (desktop, scrub)
   * ======================================================================== */

  // i-6dd95eb8 / t-54537397 — collab : les visuels glissent à des vitesses différentes.
  // Écart volontaire : IX3 partageait UNE timeline entre toutes les sections collab de
  // la page (3 sur la home) → elles se disputaient la progression quand deux sections
  // étaient visibles. Ici chaque section pilote ses propres visuels (rendu identique
  // tant qu'une seule section est à l'écran).
  define(DESKTOP, '.collab_wrapper', (el) => {
    const tl = gsap.timeline({ paused: true });
    tl.to($$('.collab_image-overlay', el), ix({ y: '-10rem', duration: 0.5, ease: 'none' }), 0);
    tl.to($$('.collab_image-2', el), ix({ y: '30rem', duration: 0.5, ease: 'none' }), 0);
    tl.to($$('.collab_image-1', el), ix({ y: '5rem', duration: 0.5, ease: 'none' }), 0);
    return { tl, st: scrubWith(el, tl, 'clamp(top bottom)', 'clamp(bottom top)', true) };
  });

  // i-afc5bd6a / t-0b4b9e9f — images superposées : parallaxe gauche / droite
  define(DESKTOP, '.section_images-overlap', (el) => {
    const tl = gsap.timeline({ paused: true });
    tl.to($$('.overlap_image.is-left', el), ix({ y: '6rem', duration: 0.5, ease: 'none' }), 0);
    tl.to($$('.overlap_image.is-right', el), ix({ y: '-6rem', duration: 0.5, ease: 'power3.out' }), 0);
    return { tl, st: scrubWith(el, tl, 'clamp(30% bottom)', 'clamp(bottom top)', true) };
  });

  // i-da536fd6 / t-cf1d2131 — « Notre histoire » (À propos)
  define(DESKTOP, '.section_the-story', (el) => {
    const tl = gsap.timeline({ paused: true });
    tl.to($$('.story_image-left', el), ix({ y: '20rem', duration: 0.5, ease: 'none' }), 0.13);
    tl.to($$('.story_image-right', el), ix({ y: '-10rem', duration: 0.5, ease: 'none' }), 0.14);
    return { tl, st: scrubWith(el, tl, 'clamp(top bottom)', 'clamp(bottom top)', true) };
  });

  // i-81fd88e8 / t-5f5a63d3 — témoignages (À propos)
  define(DESKTOP, '.section_testimonial', (el) => {
    const tl = gsap.timeline({ paused: true });
    tl.to($$('.testimonial_card.is-top', el), ix({ y: '20rem', duration: 0.5, ease: 'none' }), 0);
    tl.to($$('.testimonial_card.is-bottom', el), ix({ y: '-10rem', duration: 0.5, ease: 'none' }), 0);
    return { tl, st: scrubWith(el, tl, 'clamp(top bottom)', 'clamp(bottom top)', true) };
  });

  // i-7d2d2dca / t-c00ed30d — vidéo du hero (home) : s'élargit, descend, le texte s'efface
  define(DESKTOP, '.hero_video-wrapper', (el) => {
    const tl = gsap.timeline({ paused: true });
    tl.to(el, ix({ width: '100%', duration: 1, ease: 'power2.out' }), 0.01);
    tl.to(el, ix({ y: '9.22rem', duration: 0.49, ease: 'none' }), 0.01);
    tl.to($$('.hero_video-content', el), ix({ opacity: 0, duration: 0.3, ease: 'none' }), 0.01);
    return { tl, st: scrubWith(el, tl, 'clamp(top bottom)', 'clamp(bottom top)', 1) };
  });

  // i-aa301bd9 / t-f532756b — footer : le contenu « descend » en fin de page.
  // scrub: 0 = lié directement au scroll, sans lissage.
  define(DESKTOP, '.padding-global.is-footer', (el) => {
    const tl = gsap.timeline({ paused: true });
    tl.fromTo(el, { yPercent: -100 }, ix({ yPercent: 0, duration: 0.1, ease: 'none' }), 0);
    return { tl, st: scrubWith(el, tl, 'clamp(bottom bottom)', 'clamp(bottom top)', 0) };
  });

  /* ===========================================================================
   * IX3 — interactions actives sur tous les breakpoints
   * ======================================================================== */

  // i-7eb238fa / t-de07e3b3 — fiche produit : quand on a dépassé le bas du bloc produit,
  // la barre de miniatures descend et disparaît ; elle revient en remontant.
  define(ALL, '.product-header_component', (el) => {
    const sliders = $$('.product-header_slider-wrapper', el);
    const tl = gsap.timeline({ paused: true });
    tl.to(sliders, ix({ y: '4rem', duration: 0.3, ease: 'power3.out' }), 0);
    tl.to(sliders, ix({ display: 'none', duration: 0.3, ease: 'power3.out' }), 0);
    const st = ScrollTrigger.create({
      trigger: el,
      start: 'clamp(bottom bottom)',
      end: 'clamp(top top)',
      onLeave: () => tl.play(),
      onEnterBack: () => tl.reverse(),
    });
    return { tl, st };
  });

  // i-7174136a / t-220ab379 — accordéons FAQ (sections FAQ + blocs accordéon produit).
  // Clic = aller/retour, à vitesse 1.5.
  define(
    ALL,
    '.faq_item',
    (el) => {
      const plus = $$('.faq_plus', el);
      const tl = gsap.timeline({ paused: true });
      tl.to($$('.faq_item-body', el), ix({ opacity: 1, height: 'auto', duration: 0.5, ease: 'power3.out' }), 0);
      tl.to(
        $$('.faq_icon-wrapper', el),
        ix({ backgroundColor: 'hsla(0, 0.00%, 100.00%, 1.00)', duration: 0.3, ease: 'power1.out' }),
        0
      );
      tl.to(
        plus,
        ix({ backgroundColor: 'hsla(345.00000000000006, 5.41%, 14.51%, 1.00)', duration: 0.3, ease: 'power1.out' }),
        0
      );
      tl.to(plus, ix({ rotation: '-90deg', duration: 0.3, ease: 'power1.out' }), 0);
      return { tl };
    },
    { click: (tl) => control.togglePlayReverse(tl, 1.5) }
  );

  // i-bd3c7347 / t-4bbdbe41 — description produit « Lire plus / Réduire ».
  // Retirer la classe .product-header_description lève le line-clamp à 3 lignes.
  // Sert aussi à la description de collection (sections/collection_hero.liquid, [data-read-more]).
  define(
    ALL,
    '.read-more-btn',
    (el) => {
      const block = el.closest('[li-block="Description"], [data-read-more]') || document;
      const descriptions = $$('.product-header_description', block);
      const toggleClamp = () => descriptions.forEach((d) => d.classList.toggle('product-header_description'));
      const tl = gsap.timeline({ paused: true });
      tl.to(descriptions, ix({ opacity: 1, duration: 0.22, ease: 'power1.out' }), 0);
      tl.to({}, { duration: 0.001, onComplete: toggleClamp, onReverseComplete: toggleClamp }, 0.001);
      tl.to(
        $$('.is-read-more, .read-more_btn-image', el),
        ix({ opacity: 0, display: 'none', duration: 0.14, ease: 'power1.out' }),
        0
      );
      tl.to($$('.is-read-less', el), ix({ display: 'block', duration: 0.14, ease: 'power1.out' }), 0.14);
      return { tl };
    },
    { click: (tl) => control.togglePlayReverse(tl) }
  );

  /* ===========================================================================
   * IX3 — survols (desktop)
   * ======================================================================== */

  // i-0ec68176 / t-bc1ff505 — boutons : le libellé sort par le haut et revient par le bas
  define(
    DESKTOP,
    '.button',
    (el) => {
      const texts = $$('.button-text', el);
      const tl = gsap.timeline({ paused: true });
      if (texts.length) {
        tl.fromTo(texts, { opacity: 1, yPercent: 0 }, ix({ yPercent: -100, duration: 0.1, ease: 'power3.out' }), 0);
        tl.fromTo(
          texts,
          { opacity: 1, yPercent: 100 },
          ix({ opacity: 1, yPercent: 0, duration: 0.1, ease: 'power3.out', immediateRender: false }),
          0.1
        );
      }
      return { tl };
    },
    { hover: {} }
  );

  // i-8ee9e4e0 / t-88407a02 — cartes produit : bordure, lien et zoom de l'image
  define(
    DESKTOP,
    '.product-item_component',
    (el) => {
      const tl = gsap.timeline({ paused: true });
      tl.fromTo(el, { borderColor: 'white' }, ix({ borderColor: 'black', duration: 0.3, ease: 'none' }), 0);
      tl.fromTo(
        $$('.product-item_text-link', el),
        { opacity: 0 },
        ix({ opacity: 1, duration: 0.3, ease: 'none' }),
        0
      );
      tl.fromTo($$('.product-item_image', el), { scale: 1 }, ix({ scale: 1.1, duration: 0.3, ease: 'power1.out' }), 0);
      return { tl };
    },
    { hover: {} }
  );

  // i-201056b8 / t-38895706 — images communauté : la poignée remonte
  define(
    DESKTOP,
    '.community-image_wrapper',
    (el) => {
      const tl = gsap.timeline({ paused: true });
      const handles = $$('.community-image_handle', el);
      if (handles.length) {
        tl.fromTo(handles, { yPercent: 100 }, ix({ opacity: 1, yPercent: 0, duration: 0.5, ease: 'power3.out' }), 0);
      }
      return { tl };
    },
    { hover: {} }
  );

  // i-6dc9ac84 / t-0ec882ac — colonnes de collections : zoom, titre, overlay.
  // La sortie se joue à vitesse 1.5.
  // Correctif (absent d'IX3) : agrandi ×3,5 (×1,5 dans les petites colonnes), un
  // titre long débordait de la colonne (« CASSETTE PLAYERS »). S'il ne tient pas,
  // il passe à un mot par ligne, centré ; si un mot seul déborde encore
  // (« Kassettenspieler »), l'agrandissement est réduit juste assez.
  define(
    DESKTOP,
    '.list-collection_column',
    (el) => {
      const box = el.querySelector('.list-collection_name-left, .list-collection_name-left-small');
      const name = box && box.querySelector('.list-collection_name');
      const maxScale = box && box.classList.contains('list-collection_name-left-small') ? 1.5 : 3.5;
      let fit = { scale: maxScale, stacked: false };

      // Largeur du mot le plus long + espaces géants : un mot par ligne, centré.
      const stack = (on) => {
        name.style.width = on ? 'min-content' : '';
        name.style.wordSpacing = on ? '100vw' : '';
        name.style.textAlign = on ? 'center' : '';
      };

      // Mesure au repos (titre sur une ligne, échelle 1). Une fois agrandi, le titre
      // garde une demi-gouttière (padding de la boîte / 2) de chaque côté.
      const measure = () => {
        name.style.width = 'max-content';
        const line = name.getBoundingClientRect().width;
        stack(true);
        const words = name.getBoundingClientRect();
        stack(false);
        const margin = parseFloat(getComputedStyle(box).paddingLeft) || 0;
        const roomW = el.clientWidth - margin;
        const roomH = el.clientHeight - margin;
        if (line * maxScale <= roomW) return { scale: maxScale, stacked: false };
        const scale = Math.min(maxScale, roomW / words.width, roomH / words.height);
        return { scale: Math.max(1, scale), stacked: true }; // jamais plus petit qu'au repos
      };

      const tl = gsap.timeline({
        paused: true,
        // Fin de la sortie : titre sur une ligne, boîte rendue à son état d'origine
        // (taille auto : elle suit la colonne si la fenêtre change de taille).
        onReverseComplete: () => {
          if (!name) return;
          stack(false);
          gsap.set(box, { clearProps: 'width,height,transform' });
        },
      });
      tl.to($$('.list-collection_image', el), ix({ scale: 1.15, duration: 0.35, ease: 'none' }), 0);
      tl.fromTo(
        $$('.list-collection_name-bottom', el),
        { yPercent: 100 },
        ix({ yPercent: 0, duration: 0.41, ease: 'none' }),
        0
      );
      if (box) {
        tl.to(
          box,
          ix({
            scale: () => fit.scale,
            width: '100%',
            height: '100%',
            duration: 0.46,
            ease: 'power3.out',
            // Après la lecture des valeurs de départ (taille du titre sur une ligne).
            onStart: () => name && stack(fit.stacked),
          }),
          0
        );
      }
      tl.fromTo(
        $$('.list-collection_overlay-color', el),
        { opacity: 0 },
        ix({ opacity: 1, duration: 0.46, ease: 'power1.out' }),
        0
      );
      return {
        tl,
        // Avant chaque survol, le titre repart sur une ligne (restart() repasse par
        // le repos). Au repos seulement, on remesure — la colonne a pu changer de
        // taille — et invalidate() fait relire l'échelle et les valeurs de départ.
        beforeEnter() {
          if (!name) return;
          stack(false);
          if (tl.progress() > 0) return;
          fit = measure();
          tl.invalidate();
        },
        revert: () => name && stack(false),
      };
    },
    { hover: { leaveSpeed: 1.5 } }
  );

  /* Interactions IX3 volontairement non portées (sans effet sur le site live) :
     - i-5b691d21 / t-e21d8775 (barre sticky ATC) : entièrement neutralisée par le
       CSS !important de blocks/sticky-atc.liquid, qui gère seul l'apparition ;
     - i-389f6787, i-b4f578fe, i-908b8c7e : timelines absentes de l'export ;
     - i-a4bdc026, i-c045b860, i-e777b2dd, i-5d3a15df : sélecteurs vides ;
     - i-c2edc591 (fermeture dropdown) : cibles d'instance de composant introuvables ;
     - i-eb7e80d3 (titre au chargement) : déclencheur sans élément, aucune cible ;
     - i-3e60208f (fermeture de la modale « rupture de stock ») : la modale a été
       supprimée du thème (fonctionnalité abandonnée). */

  /* ===========================================================================
   * IX2 — dropdowns Webflow (menu de nav). Les panneaux latéraux recherche et
 * mini-panier sont animés en CSS (sections/header.liquid) ; le sélecteur pays /
 * langue est une fenêtre <dialog> (sections/announcement.liquid).
   * components.js (ex module dropdown de webflow.js) émet COMPONENT_ACTIVE /
   * COMPONENT_INACTIVE sur le .w-dropdown à l'ouverture / fermeture : c'est ce
   * qu'écoutait IX2.
   * Chaque action list IX2 = des groupes joués l'un après l'autre. Pour les listes
   * « Open », le 1er groupe n'est appliqué qu'une fois, au chargement (état
   * initial) ; au déclenchement IX2 démarre au 2e groupe, depuis les valeurs
   * courantes.
   * ======================================================================== */

  /* Easing CSS "ease" d'IX2 = cubic-bezier(.25, .1, .25, 1). Les actions IX2 sans
     easing sont linéaires ; outQuad = power1.out ; outExpo = expo.out. */
  const cssEase = (function cubicBezier(x1, y1, x2, y2) {
    const ax = 3 * x1 - 3 * x2 + 1;
    const bx = 3 * x2 - 6 * x1;
    const cx = 3 * x1;
    const ay = 3 * y1 - 3 * y2 + 1;
    const by = 3 * y2 - 6 * y1;
    const cy = 3 * y1;
    const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
    const sampleY = (t) => ((ay * t + by) * t + cy) * t;
    const slopeX = (t) => (3 * ax * t + 2 * bx) * t + cx;
    return (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      let t = x;
      for (let i = 0; i < 8; i++) {
        const err = sampleX(t) - x;
        if (Math.abs(err) < 1e-6) return sampleY(t);
        const d = slopeX(t);
        if (Math.abs(d) < 1e-6) break;
        t -= err / d;
      }
      let lo = 0;
      let hi = 1;
      t = x;
      while (hi - lo > 1e-7) {
        if (sampleX(t) < x) lo = t;
        else hi = t;
        t = (lo + hi) / 2;
      }
      return sampleY(t);
    };
  })(0.25, 0.1, 0.25, 1);

  const running = new WeakMap(); // dropdown -> timeline en cours (une action en coupe une autre)
  function playDropdown(dropdown, build) {
    const previous = running.get(dropdown);
    if (previous) previous.kill();
    const tl = gsap.timeline();
    build(tl);
    running.set(dropdown, tl);
  }

  const dropdowns = [
    {
      // e-16/e-17 (desktop, a-7/a-8) et e-18/e-19 (tablette/mobile, a-9/a-10) — menu de nav
      matches: (dd) => dd.classList.contains('nav_menu-dropdown'),
      open(tl, dd) {
        const lists = $$('.nav_dropdown-list', dd);
        const icons = $$('.dropdown-icon', dd);
        if (isDesktop()) {
          tl.to(lists, { y: 0, duration: 0.3, ease: cssEase }, 0);
          tl.to(lists, { opacity: 1, duration: 0.2, ease: cssEase }, 0);
          tl.to(icons, { rotation: 180, duration: 0.3, ease: cssEase }, 0);
        } else {
          tl.to(lists, { height: 'auto', duration: 0.3, ease: cssEase }, 0);
          tl.to(icons, { rotation: 180, duration: 0.3, ease: cssEase }, 0);
        }
      },
      close(tl, dd) {
        const lists = $$('.nav_dropdown-list', dd);
        const icons = $$('.dropdown-icon', dd);
        if (isDesktop()) {
          tl.to(lists, { opacity: 0, duration: 0.2, ease: cssEase }, 0);
          tl.to(icons, { rotation: 0, duration: 0.3, ease: cssEase }, 0);
          tl.set(lists, { y: '-3rem' }, 0.3);
        } else {
          tl.to(lists, { height: 0, duration: 0.2, ease: cssEase }, 0);
          tl.to(icons, { rotation: 0, duration: 0.3, ease: cssEase }, 0);
        }
      },
    },
  ];

  function onDropdown(state) {
    return (e) => {
      const dd = e.target;
      if (!(dd instanceof Element) || !dd.classList.contains('w-dropdown')) return;
      const anim = dropdowns.find((d) => d.matches(dd));
      if (anim) playDropdown(dd, (tl) => anim[state](tl, dd));
    };
  }
  document.addEventListener('COMPONENT_ACTIVE', onDropdown('open'));
  document.addEventListener('COMPONENT_INACTIVE', onDropdown('close'));

  matchMedia.add(DESKTOP, () => {
    gsap.set($$('.nav_menu-dropdown .nav_dropdown-list'), { y: '-3rem', opacity: 0 });
  });
  matchMedia.add(TABLET_DOWN, () => {
    gsap.set($$('.nav_menu-dropdown .nav_dropdown-list'), { height: 0 });
  });

  /* e-30 (a-15) — bouton « Filtres » tablette/mobile de la page recherche : la
     modale (élément frère .filter_filters-wrapper) remonte de 120 % à 0. */
  const filtersButton = '.filter_tablet-filters-button[data-w-id="63614b06-d13c-4caa-b5ce-a7c7ab356686"]';
  const filterSiblings = (btn) =>
    Array.from(btn.parentElement ? btn.parentElement.children : []).filter(
      (s) => s !== btn && s.classList.contains('filter_filters-wrapper')
    );
  matchMedia.add(TABLET_DOWN, () => {
    $$(filtersButton).forEach((btn) => gsap.set(filterSiblings(btn), { yPercent: 120 }));
  });
  document.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest(filtersButton) : null;
    if (!btn || isDesktop()) return;
    const wrappers = filterSiblings(btn);
    gsap.killTweensOf(wrappers);
    gsap.to(wrappers, { yPercent: 0, duration: 0.5, ease: 'power1.out' });
  });

  /* e-3 (a-4) — carte cadeau : inclinaison 3D qui suit la souris (desktop).
     IX2 : rotateY -10°→10° sur l'axe X, rotateX 10°→-10° sur l'axe Y, position de
     repos au centre, lissage 80 (on avance de 20 % vers la cible à chaque frame). */
  matchMedia.add(DESKTOP, () => {
    const cards = $$('.gift_card[data-w-id="adcb4a87-df0b-0734-55b9-7d8ddfb6ccc9"]');
    if (!cards.length) return undefined;
    const states = cards.map((card) => ({ card, x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 }));
    const onMove = (e) => {
      states.forEach((s) => {
        if (s.card.contains(e.target)) {
          const r = s.card.getBoundingClientRect();
          s.tx = (e.clientX - r.left) / r.width;
          s.ty = (e.clientY - r.top) / r.height;
        } else {
          s.tx = 0.5;
          s.ty = 0.5;
        }
      });
    };
    const tick = () => {
      states.forEach((s) => {
        if (Math.abs(s.tx - s.x) < 1e-4 && Math.abs(s.ty - s.y) < 1e-4) return; // au repos
        s.x += (s.tx - s.x) * 0.2;
        s.y += (s.ty - s.y) * 0.2;
        gsap.set(s.card, { rotationY: -10 + 20 * s.x, rotationX: 10 - 20 * s.y });
      });
    };
    document.addEventListener('mousemove', onMove, { passive: true });
    gsap.ticker.add(tick);
    return () => {
      document.removeEventListener('mousemove', onMove);
      gsap.ticker.remove(tick);
      gsap.set(cards, { clearProps: 'transform' });
    };
  });

  /* ===========================================================================
   * Animations GSAP du thème (ex custom-animations.js, comportement inchangé)
   * ======================================================================== */

  // Hover des liens de nav (desktop) : le padding horizontal s'élargit.
  matchMedia.add(DESKTOP, () => {
    const links = $$('.nav_link');
    const handlers = links.map((link) => {
      const hasIcon = link.querySelector('.nav_shop-icon-desktop') !== null;
      const base = hasIcon ? '2rem' : '3rem';
      const hover = hasIcon ? '2.75rem' : '4rem';
      const enter = () =>
        gsap.to(link, { paddingLeft: hover, paddingRight: hover, duration: 0.5, ease: 'power2.out' });
      const leave = () =>
        gsap.to(link, { paddingLeft: base, paddingRight: base, duration: 0.5, ease: 'power2.inOut' });
      link.addEventListener('mouseenter', enter);
      link.addEventListener('mouseleave', leave);
      return { link, enter, leave };
    });
    return () => {
      handlers.forEach(({ link, enter, leave }) => {
        link.removeEventListener('mouseenter', enter);
        link.removeEventListener('mouseleave', leave);
      });
      gsap.killTweensOf(links);
      gsap.set(links, { clearProps: 'paddingLeft,paddingRight' });
    };
  });

  // Hover des catégories (home) : les autres catégories et les séparateurs s'estompent.
  (function categoryHover() {
    const links = $$('.categories_link-block');
    const separators = $$('.category-separator');
    links.forEach((link) => {
      link.addEventListener('mouseenter', () => {
        links.forEach((other) => {
          if (other !== link) gsap.to(other, { opacity: 0.1, duration: 0.5, ease: 'power2.out' });
        });
        gsap.to(separators, { opacity: 0.1, duration: 0.5, ease: 'power2.out' });
      });
      link.addEventListener('mouseleave', () => {
        gsap.to([links, separators], { opacity: 1, duration: 0.5, ease: 'power2.out' });
      });
    });
  })();

  // Titre du hero À propos : les lettres passent de 10 % à 100 % d'opacité au scroll
  // (desktop, sauf « réduire les animations »).
  matchMedia.add(DESKTOP + ' and (prefers-reduced-motion: no-preference)', () => {
    if (!SplitText) return;
    const trigger = document.querySelector('.hero_about-wrapper');
    const heading = trigger && trigger.querySelector('.heading-style-h1');
    if (!heading) return;
    const split = new SplitText(heading, { type: 'chars', mask: 'chars' });
    gsap.set(split.chars, { opacity: 0.1 });
    gsap.to(split.chars, {
      opacity: 1,
      stagger: { each: 0.2 },
      ease: 'power2.out',
      scrollTrigger: { trigger, start: 'top top', end: 'bottom bottom', scrub: 1 },
    });
  });

  /* ===========================================================================
   * Démarrage
   * Script chargé en defer : le DOM est déjà parsé. (IX3 attendait l'événement
   * load, d'où un « flash » : contenu visible, masqué, puis animé.)
   * ======================================================================== */
  activate(ALL);
  activate(DESKTOP);

  /* Comme IX3 : quand la page change de hauteur sans que la fenêtre soit
     redimensionnée (images lazy qui se chargent, contenu injecté…), on recalcule
     les positions des ScrollTriggers 210 ms après la dernière variation.
     (Les redimensionnements de fenêtre sont gérés par ScrollTrigger lui-même.) */
  if (window.ResizeObserver) {
    let burstStart = null;
    let timer = null;
    new ResizeObserver(() => {
      if (!burstStart) burstStart = window.innerWidth + 'x' + window.innerHeight;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (burstStart === window.innerWidth + 'x' + window.innerHeight) ScrollTrigger.refresh();
        burstStart = null;
      }, 210);
    }).observe(document.body);
  }

  // Rendus de sections Liquify (variante produit, filtres, recommandations…)
  document.addEventListener('liquify:sections-rendered', refresh);

  window.WARAnimations = {
    refresh,
    /** Debug : instances actives, par media query. */
    instances: () => {
      const out = {};
      scopes.forEach((scope, query) => (out[query] = scope.live.slice()));
      return out;
    },
  };
})();
